import { z } from 'zod/v4';

import { CurrencyRuntype, MonetaryValueBackendRuntype } from '../backendTypes';
import { DataSourcesDTORuntype } from '../dataSource';
import { PackagingRuntype, PriceTypeRuntype } from '../offer';
import { ScrapRulesDTOInsertRuntype, ScrapRulesDTORuntype } from '../scrap';
import { SupplierPreference } from '../supplierAndStockLocation';
import { TotalCostOfOwnershipInputDTORuntype, TotalCostOfOwnershipRuleDTORuntype } from '../totalCostOfOwnershipRules';

export type CurrencySettingDTO = z.infer<typeof CurrencySettingDTORuntype>;
export const CurrencySettingDTORuntype = z.object({
    base_currency: CurrencyRuntype,
    updated_at: z.string().datetime(),
    exchange_rates: z.record(
        CurrencyRuntype,
        z.object({
            manual_rate: z.number().nullable(),
            automatic_rate: z.number(),
        }),
    ),
});

export type CurrencySettingInputDTO = z.infer<typeof CurrencySettingInputDTORuntype>;
export const CurrencySettingInputDTORuntype = z.object({
    base_currency: CurrencyRuntype,
    exchange_rates: z.record(CurrencyRuntype, z.object({ manual_rate: z.number().nullable() })),
});

export type SourcingTemplateLimit = z.infer<typeof SourcingTemplateLimitRuntype>;
export const SourcingTemplateLimitRuntype = z.object({
    type: z.enum(['Never', 'Days']),
    number: z.number().optional(),
});

export type SourcingTemplateExcessPreference = z.infer<typeof SourcingTemplateExcessPreferenceRuntype>;
export const SourcingTemplateExcessPreferenceRuntype = z.object({
    max_ratio_percent: z.number().nullable(),
    minimum_savings: MonetaryValueBackendRuntype.nullable(),
});

export type SourcingTemplateOfferAgeLimit = z.infer<typeof SourcingTemplateOfferAgeLimitRuntype>;
export const SourcingTemplateOfferAgeLimitRuntype = z.object({
    expiring_before_validity_date: SourcingTemplateLimitRuntype,
    expired_after_validity_date: SourcingTemplateLimitRuntype,
    old_after_update: SourcingTemplateLimitRuntype,
    outdated_after_update: SourcingTemplateLimitRuntype,
});

export enum SourcingTemplateSourcingPreferenceOptions {
    Fastest = 'Fastest',
    BestPrice = 'BestPrice',
    BestPriceBy = 'BestPriceBy',
}
export const SourcingTemplateSourcingPreferenceOptionsRuntype = z.enum(SourcingTemplateSourcingPreferenceOptions);

export type SourcingTemplateLeadTimePreferences = z.infer<typeof SourcingTemplateLeadTimePreferencesRuntype>;
export const SourcingTemplateLeadTimePreferencesRuntype = z.object({
    type: SourcingTemplateSourcingPreferenceOptionsRuntype,
    target: z.string().optional(),
});

export enum SourcingTemplateInventoryPreference {
    PreferInventory = 'PreferInventory',
    Neutral = 'Neutral',
    PreferMarketOffers = 'PreferMarketOffers',
}
export const SourcingTemplateInventoryPreferenceRuntype = z.enum(SourcingTemplateInventoryPreference);

export enum SolutionComparisonCriteria {
    UnitPrice = 'UnitPrice',
    TotalPrice = 'TotalPrice',
}

const PartAlternativeStrategyRuntype = z.enum([
    'IncludePartAlternativesWithoutWarning',
    'IncludePartAlternativesAsWarning',
    'ExcludePartAlternatives',
]);

export type AbcThreshold = z.infer<typeof AbcThresholdRuntype>;
export const AbcThresholdRuntype = z.object({
    threshold_cost_percent: z.number(),
    max_items_percent: z.number(),
});

export enum AbcThresholdBasis {
    UnitPrice = 'unitPrice',
    TotalPrice = 'totalPrice',
}
export const AbcThresholdBasisRuntype = z.enum(AbcThresholdBasis);

export type AbcClassificationSettings = z.infer<typeof AbcClassificationSettingsRuntype>;
export const AbcClassificationSettingsRuntype = z.object({
    threshold_basis: AbcThresholdBasisRuntype,
    a: AbcThresholdRuntype,
    b: AbcThresholdRuntype,
    c: AbcThresholdRuntype,
});

export type SourcingTemplateSolutionPreferenceDTO = z.infer<typeof SourcingTemplateSolutionPreferenceDTORuntype>;
export const SourcingTemplateSolutionPreferenceDTORuntype = z.object({
    lead_time_preferences: SourcingTemplateLeadTimePreferencesRuntype,
    solution_comparison_criteria: z.enum(SolutionComparisonCriteria),
    offer_age_limit: SourcingTemplateOfferAgeLimitRuntype,
    inventory_preference: SourcingTemplateInventoryPreferenceRuntype,
    packaging_preferences: z.array(PackagingRuntype),
    prefer_direct_api_integrations: z.boolean(),
    excluded_price_types: z.array(PriceTypeRuntype),
    exclude_mismatched_price_points: z.boolean(),
    prioritize_preferred_manufacturers: z.boolean(),
    prefer_customer_negotiated_prices: z.boolean(),
    abc_classification_settings: AbcClassificationSettingsRuntype,
    warn_when_missing_offer_validity_dates: z.boolean().optional(),
    exclude_offers_outside_validity_window: z.boolean().optional(),
    offer_validation_window: z.object({ start: z.string(), end: z.string() }).nullable().optional(),
    part_alternative_strategy: PartAlternativeStrategyRuntype,
    declare_re_reeling_costs_as_tco: z.boolean(),
});

export enum SourcingTemplateTags {
    Sourcing = 'Sourcing',
}
export const SourcingTemplateTagsRuntype = z.enum(SourcingTemplateTags);

export type SourcingTemplateDTO = z.infer<typeof SourcingTemplateDTORuntype>;
export const SourcingTemplateDTORuntype = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    is_default: z.boolean(),
    solution_preference: SourcingTemplateSolutionPreferenceDTORuntype,
    supplier_and_stock_location_preferences: z.array(
        z.object({
            supplier_and_stock_location_id: z.string().uuid(),
            kind: z.enum(SupplierPreference),
        }),
    ),
    inventory_site_preferences: z.array(
        z.object({
            inventory_site_id: z.string().uuid(),
            kind: z.enum(SupplierPreference),
        }),
    ),
    total_cost_of_ownership_rules: TotalCostOfOwnershipRuleDTORuntype.array(),
    currency_settings: CurrencySettingDTORuntype,
    scrap_rules: ScrapRulesDTORuntype,
    data_sources: DataSourcesDTORuntype,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});

export type SolutionPreferencesInputDTO = z.infer<typeof SolutionPreferencesInputDTORuntype>;
export const SolutionPreferencesInputDTORuntype = z.object({
    lead_time_preferences: SourcingTemplateLeadTimePreferencesRuntype,
    solution_comparison_criteria: z.enum(SolutionComparisonCriteria),
    offer_age_limit: SourcingTemplateOfferAgeLimitRuntype,
    inventory_preference: SourcingTemplateInventoryPreferenceRuntype,
    packaging_preferences: z.array(PackagingRuntype),
    prefer_direct_api_integrations: z.boolean(),
    excluded_price_types: z.array(PriceTypeRuntype),
    exclude_mismatched_price_points: z.boolean(),
    prioritize_preferred_manufacturers: z.boolean(),
    prefer_customer_negotiated_prices: z.boolean(),
    abc_classification_settings: AbcClassificationSettingsRuntype,
    warn_when_missing_offer_validity_dates: z.boolean(),
    exclude_offers_outside_validity_window: z.boolean(),
    offer_validation_window: z.object({ start: z.string(), end: z.string() }).nullable(),
    part_alternative_strategy: PartAlternativeStrategyRuntype,
    declare_re_reeling_costs_as_tco: z.boolean(),
});

export type SourcingTemplateInputDTO = z.infer<typeof SourcingTemplateInputDTORuntype>;
export const SourcingTemplateInputDTORuntype = z.object({
    name: z.string(),
    description: z.string().nullable(),
    is_default: z.boolean(),
    solution_preference: SolutionPreferencesInputDTORuntype,
    supplier_and_stock_location_preferences: z.array(
        z.object({
            supplier_and_stock_location_id: z.string().uuid(),
            kind: z.enum(SupplierPreference),
        }),
    ),
    inventory_site_preferences: z.array(
        z.object({
            inventory_site_id: z.string().uuid(),
            kind: z.enum(SupplierPreference),
        }),
    ),
    total_cost_of_ownership_rules: TotalCostOfOwnershipInputDTORuntype.array(),
    currency_settings: CurrencySettingInputDTORuntype,
    scrap_rules: ScrapRulesDTOInsertRuntype,
    data_sources: DataSourcesDTORuntype,
    origin: z.string().uuid().nullable(),
});
